import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { api } from '../api/client.js';

/**
 * Passkey ceremonies, browser side.
 *
 * The options and responses are passed through the platform's WebAuthn API untouched. The one
 * thing this layer adds is distinguishing "the user cancelled" from "something failed": a
 * cancelled prompt is a normal, deliberate act and must not be reported as an error.
 */

export const passkeysSupported = browserSupportsWebAuthn;

/** Thrown when the person dismissed the platform prompt. Not a failure. */
export class PasskeyCancelled extends Error {
  constructor() {
    super('The passkey prompt was dismissed');
    this.name = 'PasskeyCancelled';
  }
}

function isCancellation(error: unknown): boolean {
  return (
    error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'AbortError')
  );
}

export async function registerPasskey(label?: string): Promise<void> {
  const challenge = await api.post('/api/v1/auth/passkeys/register/options');

  let response;
  try {
    response = await startRegistration({
      optionsJSON: challenge.options as unknown as PublicKeyCredentialCreationOptionsJSON,
    });
  } catch (error) {
    if (isCancellation(error)) throw new PasskeyCancelled();
    throw error;
  }

  await api.post('/api/v1/auth/passkeys/register', {
    challengeId: challenge.challengeId,
    response: response as unknown as Record<string, unknown>,
    ...(label === undefined ? {} : { label }),
  });
}

export async function signInWithPasskey(): Promise<void> {
  const challenge = await api.post('/api/v1/auth/passkeys/authenticate/options');

  let response;
  try {
    response = await startAuthentication({
      optionsJSON: challenge.options as unknown as PublicKeyCredentialRequestOptionsJSON,
    });
  } catch (error) {
    if (isCancellation(error)) throw new PasskeyCancelled();
    throw error;
  }

  await api.post('/api/v1/auth/passkeys/authenticate', {
    challengeId: challenge.challengeId,
    response: response as unknown as Record<string, unknown>,
  });
}
